// utils.js - Pure utility functions extracted from App.js
// These functions have no React state dependencies and can be safely reused

import katex from 'katex';

// SEED_SUBJECTS: Canonical list of Python subjects for normalization
// Used when database is empty (new student) or for fuzzy matching
export const SEED_SUBJECTS = [
  // Basics
  "Print Statements",
  "Comments",
  "Variables",
  "Data Types",
  "User Input",
  "Type Conversion",
  
  // Operators
  "Arithmetic Operators",
  "Operator Precedence",
  "Comparison Operators",
  "Logical Operators",
  "Assignment Operators",
  
  // Control Flow
  "If Statements",
  "If-Else Statements",
  "Elif Statements",
  "Conditionals",
  "While Loops",
  "For Loops",
  "Break Statement",
  "Continue Statement",
  "Nested Loops",
  
  // Data Structures
  "Strings",
  "String Methods",
  "String Formatting",
  "Lists",
  "List Methods",
  "List Indexing",
  "List Slicing",
  "Tuples",
  "Dictionaries",
  "Dictionary Methods",
  "Sets",
  
  // Functions
  "Functions",
  "Function Parameters",
  "Return Statements",
  "Default Parameters",
  "Keyword Arguments",
  "Variable Scope",
  "Lambda Functions",
  "Recursion",
  
  // Errors
  "Syntax Errors",
  "Runtime Errors",
  "Logic Errors",
  "Programming Errors",
  "Exception Handling",
  "Try-Except Blocks",
  
  // OOP
  "Classes",
  "Objects",
  "Constructors",
  "Methods",
  "Attributes",
  "Inheritance",
  
  // Files & Modules
  "File Reading",
  "File Writing",
  "Modules",
  "Imports"
];

// Get current timestamp in EST timezone formatted as "YYYY-MM-DD HH:MM:SS"
export const getESTTimestamp = () => {
  const options = {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(new Date());
  const map = new Map(parts.map(p => [p.type, p.value]));
  return `${map.get('year')}-${map.get('month')}-${map.get('day')} ${map.get('hour')}:${map.get('minute')}:${map.get('second')}`;
};

// Get current time as Date object in EST (for consistent date calculations with backend)
export const getESTNow = () => {
  // Create a date string in EST, then parse it back to get EST-equivalent Date
  const estString = getESTTimestamp(); // "YYYY-MM-DD HH:MM:SS" in EST
  return new Date(estString.replace(' ', 'T'));
};

// Normalize count value to integer (handles string/number/NaN)
export const normalizeCount = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

// Detect flip-flopping pattern from history entries
// Returns { isFlipFlopping, alternationRatio, maxStreak }
export const detectFlipFlopping = (historyEntries) => {
  if (!historyEntries || historyEntries.length < 6) {
    return { isFlipFlopping: false, alternationRatio: 0 };
  }

  // Sort by timestamp (most recent first) to analyze pattern
  // Handle different field names: timestamp, date, or recorded_at
  const sorted = [...historyEntries].sort((a, b) => 
    new Date(b.timestamp || b.date || b.recorded_at || 0) - new Date(a.timestamp || a.date || a.recorded_at || 0)
  );

  // Look at the sequence of correct/wrong answers
  let alternations = 0;
  let maxStreak = 1;
  let currentStreak = 1;
  
  for (let i = 1; i < sorted.length; i++) {
    const prevCorrect = (sorted[i-1].correct || 0) > 0;
    const currCorrect = (sorted[i].correct || 0) > 0;
    
    if (prevCorrect !== currCorrect) {
      alternations++;
      currentStreak = 1;
    } else {
      currentStreak++;
      maxStreak = Math.max(maxStreak, currentStreak);
    }
  }

  const alternationRatio = alternations / (sorted.length - 1);
  
  // Flip-flopping: high alternation ratio (≥60%), short max streak (≤2)
  const isFlipFlopping = alternationRatio >= 0.6 && maxStreak <= 2;

  console.log(`🔄 Flip-flop check: ${alternations} alternations in ${sorted.length} entries, ratio=${(alternationRatio*100).toFixed(0)}%, maxStreak=${maxStreak} → ${isFlipFlopping ? 'FLIP-FLOPPING' : 'Consistent'}`);

  return { isFlipFlopping, alternationRatio, maxStreak };
};

// Detect trend (improving vs declining) from history entries
// Returns { trend, recentAccuracy, earlyAccuracy, accuracyDiff }
export const detectTrend = (historyEntries) => {
  if (!historyEntries || historyEntries.length < 4) {
    return { trend: 'stable', recentAccuracy: null, earlyAccuracy: null };
  }

  // Sort by timestamp (oldest first for chronological order)
  // Handle different field names: timestamp, date, or recorded_at
  const sorted = [...historyEntries].sort((a, b) => 
    new Date(a.timestamp || a.date || a.recorded_at || 0) - new Date(b.timestamp || b.date || b.recorded_at || 0)
  );

  // Split into first half (early) and second half (recent)
  const midpoint = Math.floor(sorted.length / 2);
  const earlyHalf = sorted.slice(0, midpoint);
  const recentHalf = sorted.slice(midpoint);

  // Calculate accuracy for each half
  const earlyCorrect = earlyHalf.reduce((sum, e) => sum + (e.correct || 0), 0);
  const earlyTotal = earlyHalf.length;
  const earlyAccuracy = earlyTotal > 0 ? (earlyCorrect / earlyTotal) * 100 : 0;

  const recentCorrect = recentHalf.reduce((sum, e) => sum + (e.correct || 0), 0);
  const recentTotal = recentHalf.length;
  const recentAccuracy = recentTotal > 0 ? (recentCorrect / recentTotal) * 100 : 0;

  const accuracyDiff = recentAccuracy - earlyAccuracy;

  let trend = 'stable';
  if (accuracyDiff <= -25) {
    trend = 'declining';  // Significant drop (e.g., 6/6 early → 0/4 recent = 100% → 0%)
  } else if (accuracyDiff >= 25) {
    trend = 'improving';  // Significant improvement
  } else if (accuracyDiff <= -15) {
    trend = 'slipping';   // Moderate decline
  } else if (accuracyDiff >= 15) {
    trend = 'progressing'; // Moderate improvement
  }

  console.log(`📈 Trend check: Early ${earlyAccuracy.toFixed(0)}% (${earlyCorrect}/${earlyTotal}) → Recent ${recentAccuracy.toFixed(0)}% (${recentCorrect}/${recentTotal}) = ${trend.toUpperCase()} (${accuracyDiff > 0 ? '+' : ''}${accuracyDiff.toFixed(0)}%)`);

  return { trend, recentAccuracy, earlyAccuracy, accuracyDiff };
};

// Calculate hint level based on accuracy, history patterns, and questionAnswered data
// questionAnsweredData: { correct, wrong } from informal Q&A (weighted at 0.5x quiz data)
export const calculateHintLevel = (correct, wrong, historyEntries = null, questionAnsweredData = null) => {
  // Combine quiz data with questionAnswered data (weighted at 0.5x)
  let effectiveCorrect = correct;
  let effectiveWrong = wrong;
  
  if (questionAnsweredData) {
    const qaCorrect = questionAnsweredData.correct || 0;
    const qaWrong = questionAnsweredData.wrong || 0;
    const qaWeight = 0.5; // Informal Q&A counts as half-weight evidence
    effectiveCorrect += qaCorrect * qaWeight;
    effectiveWrong += qaWrong * qaWeight;
    if (qaCorrect + qaWrong > 0) {
      console.log(`📝 Hint calc: Adding questionAnswered data (${qaCorrect}✓ ${qaWrong}✗ × ${qaWeight} weight)`);
    }
  }
  
  const total = effectiveCorrect + effectiveWrong;
  if (total === 0) return { accuracy: 0, hintLevel: 'moderate', label: 'No Data', isFlipFlopping: false, trend: 'stable' };

  const accuracy = (effectiveCorrect / total) * 100;

  // Require at least 3 data points before trusting accuracy — avoids 100% from 1 lucky answer
  if (total < 3) {
    console.log(`⚠️ Limited data (${total} questions) — defaulting to MODERATE hints regardless of ${accuracy.toFixed(0)}% accuracy`);
    return { accuracy, hintLevel: 'moderate', label: 'Limited Data', isFlipFlopping: false, trend: 'stable' };
  }
  
  // Check for patterns if history entries are provided
  let flipFlopInfo = { isFlipFlopping: false, alternationRatio: 0 };
  let trendInfo = { trend: 'stable', recentAccuracy: null, earlyAccuracy: null };
  
  if (historyEntries && historyEntries.length >= 4) {
    trendInfo = detectTrend(historyEntries);
  }
  if (historyEntries && historyEntries.length >= 6) {
    flipFlopInfo = detectFlipFlopping(historyEntries);
  }

  // Priority 1: Flip-flopping with mediocre accuracy → DETAILED
  if (flipFlopInfo.isFlipFlopping && accuracy < 80) {
    console.log(`⚠️ Flip-flop detected with ${accuracy.toFixed(0)}% accuracy → Elevating to DETAILED hints`);
    return { 
      accuracy, 
      hintLevel: 'detailed', 
      label: 'Inconsistent', 
      isFlipFlopping: true,
      trend: trendInfo.trend,
      alternationRatio: flipFlopInfo.alternationRatio
    };
  }

  // Priority 2: Declining trend (started well, now struggling) → DETAILED
  if ((trendInfo.trend === 'declining' || trendInfo.trend === 'slipping') && accuracy < 80) {
    const label = trendInfo.trend === 'declining' ? 'Declining' : 'Slipping';
    console.log(`📉 ${label} trend detected (${trendInfo.earlyAccuracy?.toFixed(0)}% → ${trendInfo.recentAccuracy?.toFixed(0)}%) → Elevating to DETAILED hints`);
    return { 
      accuracy, 
      hintLevel: 'detailed', 
      label: label, 
      isFlipFlopping: false,
      trend: trendInfo.trend,
      recentAccuracy: trendInfo.recentAccuracy,
      earlyAccuracy: trendInfo.earlyAccuracy
    };
  }

  // Priority 3: Improving trend → can reduce hints slightly
  if ((trendInfo.trend === 'improving' || trendInfo.trend === 'progressing') && accuracy >= 50) {
    const hintLevel = accuracy >= 70 ? 'minimal' : 'moderate';
    console.log(`📈 Improving trend detected → Rewarding with ${hintLevel.toUpperCase()} hints`);
    return { 
      accuracy, 
      hintLevel: hintLevel, 
      label: 'Improving', 
      isFlipFlopping: false,
      trend: trendInfo.trend,
      recentAccuracy: trendInfo.recentAccuracy,
      earlyAccuracy: trendInfo.earlyAccuracy
    };
  }
  
  // Default: Base on overall accuracy (include trend info for display)
  if (accuracy >= 80) {
    return { 
      accuracy, 
      hintLevel: 'minimal', 
      label: 'Mastered', 
      isFlipFlopping: false, 
      trend: trendInfo.trend,
      recentAccuracy: trendInfo.recentAccuracy,
      earlyAccuracy: trendInfo.earlyAccuracy
    };
  } else if (accuracy >= 50) {
    return { 
      accuracy, 
      hintLevel: 'moderate', 
      label: 'Learning', 
      isFlipFlopping: false, 
      trend: trendInfo.trend,
      recentAccuracy: trendInfo.recentAccuracy,
      earlyAccuracy: trendInfo.earlyAccuracy
    };
  } else {
    return { 
      accuracy, 
      hintLevel: 'detailed', 
      label: 'Struggling', 
      isFlipFlopping: false, 
      trend: trendInfo.trend,
      recentAccuracy: trendInfo.recentAccuracy,
      earlyAccuracy: trendInfo.earlyAccuracy
    };
  }
};

// Calculate memory strength based on spaced repetition / forgetting curve
// Uses Ebbinghaus forgetting curve: R = e^(-t/S) where t = time, S = stability
// softReviewCount: number of soft reviews in last 7 days (0.5 day boost each)
export const calculateMemoryStrength = (subject, historyEntries, aggregateData, softReviewCount = 0) => {
  if (!historyEntries || historyEntries.length === 0) {
    // If no history but has aggregate, treat as "long ago" practice
    if (aggregateData) {
      const total = (aggregateData.correct || 0) + (aggregateData.wrong || 0);
      if (total > 0) {
        return {
          subject,
          memoryStrength: 30, // Assume significant decay if no date info
          daysSinceReview: 999,
          reviewCount: total,
          softReviewCount,
          needsReview: true,
          urgency: 'critical',
          daysUntilReview: 0,
          accuracy: Math.round((aggregateData.correct / total) * 100),
          lastPracticeDate: null
        };
      }
    }
    return null;
  }

  // Sort by date (most recent first)
  // Handle different field names: date, recorded_at, timestamp, last_updated
  const sorted = [...historyEntries].sort((a, b) => 
    new Date(b.date || b.recorded_at || b.timestamp || b.last_updated || 0) - new Date(a.date || a.recorded_at || a.timestamp || a.last_updated || 0)
  );

  const now = getESTNow(); // Use EST for consistent calculation with backend dates
  const rawDate = sorted[0].date || sorted[0].recorded_at || sorted[0].timestamp || sorted[0].last_updated;
  
  // Fix timezone issue: If date is "YYYY-MM-DD" format (no time), append time to avoid UTC midnight interpretation
  // This prevents dates from showing as previous day due to UTC->EST conversion
  let lastPractice;
  if (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
    // Date-only string: parse as local time by adding T12:00:00 (noon)
    lastPractice = new Date(rawDate + 'T12:00:00');
  } else if (typeof rawDate === 'string' && rawDate.includes('T') && rawDate.endsWith('Z')) {
    // UTC timestamp: convert to local
    lastPractice = new Date(rawDate);
  } else {
    lastPractice = new Date(rawDate);
  }
  
  let daysSince = Math.max(0, (now - lastPractice) / (1000 * 60 * 60 * 24));

  // Apply soft review boost: each soft review in last 7 days reduces effective days by 0.5
  const softReviewBoost = softReviewCount * 0.5;
  const effectiveDaysSince = Math.max(0, daysSince - softReviewBoost);

  // Calculate accuracy from history
  let totalCorrect = 0;
  let totalWrong = 0;
  sorted.forEach(entry => {
    totalCorrect += entry.correct || 0;
    totalWrong += entry.wrong || 0;
  });
  const total = totalCorrect + totalWrong;
  const accuracy = total > 0 ? (totalCorrect / total) : 0;

  // Number of practice sessions (builds "stability")
  const reviewCount = sorted.length;

  // Memory decay formula (simplified Ebbinghaus)
  // More reviews = slower decay rate (stability increases)
  // Higher accuracy = stronger initial encoding
  const stabilityFactor = Math.min(1, 0.3 + (reviewCount * 0.1)); // 0.3 to 1.0
  const accuracyFactor = 0.5 + (accuracy * 0.5); // 0.5 to 1.0
  const decayRate = Math.max(0.02, 0.30 - (stabilityFactor * 0.25)); // Slower decay with more reviews
  
  // Retention = initial_strength * e^(-decay * effectiveDays)
  // Use effectiveDaysSince (with soft review boost applied)
  const initialStrength = accuracy * accuracyFactor;
  const retention = initialStrength * Math.exp(-decayRate * effectiveDaysSince);
  const memoryStrength = Math.round(Math.min(100, Math.max(0, retention * 100)));

  // Debug logging for memory strength calculation
  if (softReviewCount > 0) {
    console.log(`🧮 Memory Calc [${subject}]: days=${daysSince.toFixed(2)}, softReviews=${softReviewCount}, boost=${softReviewBoost.toFixed(2)}, effectiveDays=${effectiveDaysSince.toFixed(2)}, acc=${(accuracy * 100).toFixed(1)}%, strength=${memoryStrength}%`);
  } else {
    console.log(`🧮 Memory Calc [${subject}]: days=${daysSince.toFixed(2)}, acc=${(accuracy * 100).toFixed(1)}%, reviews=${reviewCount}, decay=${decayRate.toFixed(3)}, strength=${memoryStrength}%`);
  }

  // Optimal review interval (spaced repetition: 1, 2, 4, 8, 16... days)
  // Optimal review interval - aligned with decay rate
  // 1-2 reviews: 1-2 days, 5 reviews: 3-4 days, 10 reviews: 7 days, 20 reviews: 10-12 days
  const optimalInterval = Math.min(12, Math.max(1, Math.floor(reviewCount * 0.6)));
  const daysUntilReview = Math.max(0, optimalInterval - daysSince);

  // Determine urgency
  let urgency = 'healthy';
  let needsReview = false;
  if (memoryStrength < 40) {
    urgency = 'critical';
    needsReview = true;
  } else if (memoryStrength < 60) {
    urgency = 'soon';
    needsReview = true;
  } else if (memoryStrength < 75) {
    urgency = 'upcoming';
    needsReview = false;
  }

  return {
    subject,
    memoryStrength,
    daysSinceReview: Math.round(daysSince),
    reviewCount,
    softReviewCount,
    needsReview,
    urgency,
    daysUntilReview: Math.round(daysUntilReview),
    accuracy: Math.round(accuracy * 100),
    lastPracticeDate: lastPractice.toLocaleDateString('en-US', { timeZone: 'America/New_York' })
  };
};

// Render message content - strips internal tags and renders LaTeX
export const renderMessageContent = (content) => {
  if (!content) return '';
  // Remove [PROGRESS_START]...[PROGRESS_END]
  let cleaned = content.replace(/\[PROGRESS_START\][\s\S]*?\[PROGRESS_END\]/g, '');
  // Also remove if it's just [PROGRESS_START] without end (streaming or partial)
  cleaned = cleaned.replace(/\[PROGRESS_START\][\s\S]*/g, '');
  
  // Remove [PLAN_START]...[PLAN_END]
  cleaned = cleaned.replace(/\[PLAN_START\][\s\S]*?\[PLAN_END\]/g, '');
  cleaned = cleaned.replace(/\[PLAN_START\][\s\S]*/g, '');

  // Remove [CODE_START]...[CODE_END]
  cleaned = cleaned.replace(/\[CODE_START\][\s\S]*?\[CODE_END\]/g, '');
  cleaned = cleaned.replace(/\[CODE_START\][\s\S]*/g, '');

  // Remove [STEP_COMPLETE] and the step text that follows on the same line
  cleaned = cleaned.replace(/\[STEP_COMPLETE\][^\n]*/g, '');
  
  // Remove [REVISES_STEP: N] tags
  cleaned = cleaned.replace(/\[REVISES_STEP:\s*\d+\]/g, '');

  // Remove internal tracking tags (not for student display)
  cleaned = cleaned.replace(/\[INFORMAL_RESULT\][\s\S]*?\[\/INFORMAL_RESULT\]/g, '');
  cleaned = cleaned.replace(/\[QUIZ_RESULT\][\s\S]*?\[\/QUIZ_RESULT\]/g, '');
  cleaned = cleaned.replace(/\[CODE_DETECTED\][\s\S]*?\[\/CODE_DETECTED\]/g, '');

  // Remove HMM Knowledge State context block (internal AI instruction, not for student display)
  cleaned = cleaned.replace(/\n\n\[HMM Knowledge State:[\s\S]*?\]\nPlease tailor[^\n]*/g, '');
  cleaned = cleaned.replace(/\[HMM Knowledge State:[\s\S]*?\]\nPlease tailor[^\n]*/g, '');

  // Remove internal LLM instructions (from "Audit Code" button) - show just "Here's my code:" instead
  cleaned = cleaned.replace(/Please audit my code against our \[CURRICULUM LOCKDOWN\] rules[\s\S]*?Here's my code:/g, "Here's my code:");

  // FIX PYTHON CODE INDENTATION: Remove unwanted leading spaces from code blocks
  // This fixes LLM outputting code like:
  //    print(x)
  //    print(y)
  // When it should be:
  // print(x)
  // print(y)
  cleaned = cleaned.replace(/```python\n([\s\S]*?)```/g, (match, codeContent) => {
    const lines = codeContent.split('\n');
    // Find minimum indentation (ignoring empty lines)
    let minIndent = Infinity;
    for (const line of lines) {
      if (line.trim().length > 0) {
        const leadingSpaces = line.match(/^(\s*)/)[1].length;
        if (leadingSpaces < minIndent) {
          minIndent = leadingSpaces;
        }
      }
    }
    // Remove common leading whitespace from all lines
    if (minIndent > 0 && minIndent < Infinity) {
      const dedentedLines = lines.map(line => {
        if (line.trim().length === 0) return line; // Keep empty lines as-is
        return line.substring(minIndent);
      });
      return '```python\n' + dedentedLines.join('\n') + '```';
    }
    return match; // Return unchanged if no common indent
  });

  // Collapse multiple consecutive blank lines into a single blank line
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');

  // Render LaTeX math formulas using KaTeX
  // Display math: \[...\] or $$...$$
  cleaned = cleaned.replace(/\\\[([\s\S]*?)\\\]/g, (match, formula) => {
    try {
      return katex.renderToString(formula.trim(), { displayMode: true, throwOnError: false });
    } catch (e) {
      return match; // Return original if parsing fails
    }
  });
  cleaned = cleaned.replace(/\$\$([\s\S]*?)\$\$/g, (match, formula) => {
    try {
      return katex.renderToString(formula.trim(), { displayMode: true, throwOnError: false });
    } catch (e) {
      return match;
    }
  });
  // Inline math: \(...\) or $...$
  cleaned = cleaned.replace(/\\\(([\s\S]*?)\\\)/g, (match, formula) => {
    try {
      return katex.renderToString(formula.trim(), { displayMode: false, throwOnError: false });
    } catch (e) {
      return match;
    }
  });
  cleaned = cleaned.replace(/\$([^$\n]+?)\$/g, (match, formula) => {
    try {
      return katex.renderToString(formula.trim(), { displayMode: false, throwOnError: false });
    } catch (e) {
      return match;
    }
  });

  return cleaned.trim();
};

// Detect if text contains Python code (vs natural language)
// Returns true if the text appears to be code
export const detectCodeInText = (text, logger = { debug: () => {} }) => {
  if (!text || text.trim().length < 5) return false;
  
  // Strong indicators - single occurrence is enough (definitely code)
  const strongCodePatterns = [
    /^\s*(def|class)\s+\w+\s*[\(:]/, // Function or class definition
    /^\s*import\s+\w+/m,  // Import statement
    /^\s*from\s+\w+\s+import/m,  // From import
    /```python/,  // Explicit Python code block
    /```[\s\S]*```/,  // Any markdown code block
    /^\s*(if|for|while|try|with)\s+.*:\s*$/m,  // Control structures with colon
    /^\s*(elif|else|except|finally)\s*.*:\s*$/m,  // Continuation blocks
  ];
  
  for (const pattern of strongCodePatterns) {
    if (pattern.test(text)) {
      logger.debug('🔍 Code detected (strong pattern):', pattern);
      return true;
    }
  }
  
  // Single-line code patterns - any one of these indicates code
  const singleLineCodePatterns = [
    /^\s*print\s*\(/m,  // print() function call
    /^\s*input\s*\(/m,  // input() function call
    /^\s*\w+\s*=\s*(?:int|float|str|input|len|range|True|False|None|\d|"|'|\[|\{)/m,  // Variable assignment to code values
    /^\s*\w+\s*\+=|-=|\*=|\/=\s*.+/m,  // Augmented assignment
    /^\s*return\s+.+/m,  // Return statement
    /^\s*#\s*\w+/m,  // Python comment (# followed by text)
    /\bint\s*\(|\bstr\s*\(|\bfloat\s*\(|\bbool\s*\(/,  // Type conversion functions
    /\brange\s*\(|\blen\s*\(|\blist\s*\(|\bdict\s*\(|\bset\s*\(/,  // Built-in functions
    /\binput\s*\(|\bprint\s*\(/,  // I/O functions
    /\[\s*\d+.*\]/,  // List literal with numbers [1, 2, ...]
    /\{\s*["']\w+["']\s*:\s*.+\}/,  // Dictionary literal {"key": value}
    /\bTrue\b|\bFalse\b|\bNone\b/,  // Python constants
    /\w+\s*==\s*\w+|\w+\s*!=\s*\w+|\w+\s*<=\s*\w+|\w+\s*>=\s*\w+/,  // Comparison with variables
  ];
  
  // Count how many single-line patterns match
  let matchCount = 0;
  for (const pattern of singleLineCodePatterns) {
    if (pattern.test(text)) {
      matchCount++;
      if (matchCount >= 1) {
        // Check if it's likely conversational vs actual code
        // Exclude if it reads like natural language
        const lowerText = text.toLowerCase();
        const conversationalPhrases = [
          'i want to', 'i need to', 'how do i', 'how to', 'can you', 'please',
          'help me', 'show me', 'explain', 'what is', 'what does', 'why does',
          'i\'m trying', 'i am trying', 'i would like', 'could you',
          'get the', 'calculate the', 'find the', 'print the', 'display the',
          'of the', 'of a', 'and calculate', 'and print', 'and display',
          ' the area', ' the width', ' the length', ' the result',
          'rectangle', 'circle', 'triangle', 'square'
        ];
        
        const isConversational = conversationalPhrases.some(phrase => lowerText.includes(phrase));
        
        // Also check for English article patterns (the, a, an followed by noun)
        const hasEnglishPattern = /\b(the|a|an)\s+\w+\s+(and|or|of)\b/i.test(text);
        
        if (!isConversational && !hasEnglishPattern) {
          logger.debug('🔍 Code detected (single-line pattern):', pattern);
          return true;
        }
      }
    }
  }
  
  // Multi-line check: if we have indented lines, it's likely code
  const lines = text.split('\n');
  if (lines.length > 1) {
    let indentedLines = 0;
    let codeLines = 0;
    
    for (const line of lines) {
      if (line.match(/^\s{2,}\S/)) indentedLines++;  // Indented line
      if (line.match(/^\s*\w+\s*=\s*.+/) || line.match(/^\s*(print|input|return|if|for|while)\s*[\(]/)) {
        codeLines++;
      }
    }
    
    if (indentedLines >= 1 || codeLines >= 2) {
      logger.debug('🔍 Code detected (multi-line analysis)');
      return true;
    }
  }
  
  return false;
};

// Analyze performance history and generate summary for system prompt
// Returns a formatted string describing the student's performance by subject
export const analyzePerformanceHistory = (history, aggregates = {}) => {
  if (!history || history.length === 0) {
    if (Object.keys(aggregates).length === 0) return "No history recorded yet.";
    
    return Object.entries(aggregates).map(([subject, data]) => {
      const total = data.correct + data.wrong;
      const acc = total > 0 ? (data.correct / total) : 0;
      let level = acc >= 0.8 ? "MASTERED" : acc <= 0.3 ? "STRUGGLING" : "PROVING COMPETENCE";
      return `- ${subject}: ${level} (Lifetime Accuracy: ${(acc * 100).toFixed(0)}% across ${total} total).`;
    }).join("\n");
  }

  const bySubject = {};
  history.forEach(item => {
    if (!bySubject[item.subject]) bySubject[item.subject] = [];
    bySubject[item.subject].push(item);
  });

  const subjectProfiles = [];
  const subjectsInHistory = new Set(Object.keys(bySubject));

  // Handle subjects in aggregate table but not in the last 1000 history records
  Object.entries(aggregates).forEach(([subject, data]) => {
    if (!subjectsInHistory.has(subject)) {
      const total = data.correct + data.wrong;
      if (total > 0) {
        const acc = (data.correct / total);
        let level = acc >= 0.8 ? "MASTERED" : acc <= 0.3 ? "STRUGGLING" : "PROVING COMPETENCE";
        subjectProfiles.push(`- ${subject}: ${level} (Legacy Knowledge: ${(acc * 100).toFixed(0)}% accuracy).`);
      }
    }
  });

  for (const [subject, entries] of Object.entries(bySubject)) {
    // Subject IS in history - use ONLY history data, ignore aggregate
    const sortedEntries = entries.sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      if (dateA === dateB) {
        return (a.correct + a.wrong) - (b.correct + b.wrong);
      }
      return dateA - dateB;
    });
    
    // Sum all history entries for this subject (history is NOT cumulative)
    let totalCorrect = 0;
    let totalWrong = 0;
    entries.forEach(entry => {
      totalCorrect += entry.correct || 0;
      totalWrong += entry.wrong || 0;
    });
    const totalOverall = totalCorrect + totalWrong;

    const recent = sortedEntries.slice(-10);

    if (recent.length < 2) {
      if (totalOverall === 0) {
        subjectProfiles.push(`- ${subject}: NEW TOPIC (Just started).`);
      } else {
        const status = totalCorrect > totalWrong ? "Proficient" : "Learning";
        const pct = totalOverall > 0 ? ((totalCorrect / totalOverall) * 100).toFixed(0) : 0;
        subjectProfiles.push(`- ${subject}: ${status} (Initial data: ${pct}% Accuracy across ${totalOverall} total).`);
      }
      continue;
    }

    // Build session accuracies from individual history entries
    const sessionAccuracies = entries.map(entry => {
      const total = (entry.correct || 0) + (entry.wrong || 0);
      return total > 0 ? (entry.correct || 0) / total : 0;
    });

    if (sessionAccuracies.length === 0) {
      const pct = totalOverall > 0 ? ((totalCorrect / totalOverall) * 100).toFixed(0) : 0;
      subjectProfiles.push(`- ${subject}: STABLE (${pct}% Accuracy across ${totalOverall} total).`);
      continue;
    }

    const avg = sessionAccuracies.reduce((a, b) => a + b, 0) / sessionAccuracies.length;
    const overallAccuracy = totalOverall > 0 ? totalCorrect / totalOverall : 0;

    // IMPROVED FLIP-FLOP DETECTION (same logic as Analytics dashboard)
    // Since each session is 1 question (100% or 0%), look for alternating patterns
    let isFlipFlopping = false;
    if (sessionAccuracies.length >= 6 && overallAccuracy < 0.65) {
      const binary = sessionAccuracies.map(acc => acc >= 0.5 ? 1 : 0);
      let alternations = 0;
      for (let i = 1; i < binary.length; i++) {
        if (binary[i] !== binary[i-1]) alternations++;
      }
      const alternationRatio = alternations / (binary.length - 1);
      
      let maxStreak = 1, currentStreak = 1;
      for (let i = 1; i < binary.length; i++) {
        if (binary[i] === binary[i-1]) {
          currentStreak++;
          maxStreak = Math.max(maxStreak, currentStreak);
        } else {
          currentStreak = 1;
        }
      }
      isFlipFlopping = alternationRatio >= 0.6 && maxStreak <= 2;
    }

    let trendDescription = "";
    if (isFlipFlopping) {
      trendDescription = "INCONSISTENT (Flip-flopping accuracy). Challenge their logic strictly.";
    } else if (overallAccuracy >= 0.8 && totalOverall >= 5) {
      trendDescription = "MASTERED. Use highly Socratic, minimal hints.";
    } else if (overallAccuracy >= 0.8 && totalOverall < 5) {
      trendDescription = "PROVING COMPETENCE. Keep hints moderate until more consistency is shown.";
    } else if (overallAccuracy <= 0.3) {
      trendDescription = "STRUGGLING. Provide simplified analogies and granular steps.";
    } else {
      trendDescription = "STEADY PROGRESS. Moderate guidance recommended.";
    }

    subjectProfiles.push(`- ${subject}: ${trendDescription}`);
  }

  return subjectProfiles.join("\n");
};
